/**
 * combat_core.ts — the pure rules of ground combat (combat wave, 2026-09-04).
 * MIRROR: client/src/lib/combat_core.ts (keep byte-identical — the client
 * predicts with these numbers and the server enforces them).
 *
 * Design, from the combat research report (two-wave, 180 sources):
 *  · Fairness is CADENCE-BASED on server rows (the WoW / OSRS model), never a
 *    twitch window: reach + a facing cone + a cooldown, with the reach PADDED
 *    as the latency accommodation (Blizzard's "leeway").
 *  · Weight is PRICED COMMITMENT (Sucker Punch / Mordhau): a whiff slows the
 *    next swing, heavy hits stagger, a windup past its onset has hyper armour.
 *    No stamina bar — commitment is priced in time, not a meter.
 *  · NPC attacks TELEGRAPH: a 1–1.5 s windup (Birkhead's read window) whose
 *    hit is resolved by a server-timed snapshot at windup end (FFXIV's model),
 *    decoupled from the 1.5 s AI tick; at most two attackers wind up on one
 *    player at a time (God of War's aggression tokens).
 *  · Movement grammar on constants: walk / sprint / slide inside the existing
 *    12 m/s anti-cheat envelope; a slide locks the weapon for its first beat.
 */

/** Reach padding — the row lags the capsule by the ack latency; pad, don't
 *  demand precision (Blizzard's melee leeway, confirmed intended in PvP). */
export const REACH_PAD_M = 0.6;
/** Facing cone as a cosine threshold: melee ±78° (dot > 0.2 — the Roblox
 *  server-validation pattern), guns ±45°. */
export const MELEE_CONE_COS = 0.2;
export const GUN_CONE_COS = 0.7;
/** A whiff multiplies the NEXT cooldown (Mordhau #22: a miss must be punishable). */
export const WHIFF_CD_MULT = 1.5;
/** Heavy hits stagger the target this long; a staggered NPC neither moves nor
 *  winds up, a staggered player moves at half speed and cannot strike. */
export const STAGGER_MS = 600;
/** A weapon whose BASE damage reaches this is heavy (thunder maul, nova blade,
 *  long rifle, storm cannon, aurora lance, ion blaster). */
export const HEAVY_DMG_MIN = 58;
/** A windup that has run at least this long cannot be staggered out of
 *  (hyper armour by ONSET time — Ubisoft's tuning knob). */
export const HYPER_ARMOUR_ONSET_MS = 450;
/** Aggression tokens: at most this many NPCs wind up on one player at once. */
export const AGGRO_TOKENS = 2;
/** NPC windup bounds: the 1–1.5 s read sweet spot; weaker blows come faster. */
export const WINDUP_MIN_MS = 1000;
export const WINDUP_MAX_MS = 1500;
/** No strikes in a slide's first beat — the gun lockout the movement grammar needs. */
export const SLIDE_LOCKOUT_MS = 300;
/** Movement grammar (all ≤ MAX_MOVE_SPEED_MPS 12 — the server envelope). */
export const SPEED_WALK_MPS = 6;
export const SPEED_SPRINT_MPS = 9;
export const SLIDE_PEAK_MPS = 10;
export const SLIDE_MS = 900;
/** Swimming (2026-09-11, interactive water): a slow crawl at the surface — the
 *  client enters it in water deeper than SWIM_DEPTH_M (lib/water_physics);
 *  the server only ever moves a swimmer SLOWER than a walker. */
export const SPEED_SWIM_MPS = 2.4;
/** A staggered player moves at this fraction of speed. */
export const STAGGER_SPEED_MULT = 0.5;
/** Crouch (build-now wave 2, 2026-09-12): the low stance — Ctrl when not
 *  sprinting toggles it; a crouched player walks at this speed, never
 *  sprints or slides, and stands only where the standing capsule fits. */
export const SPEED_CROUCH_MPS = 3;
/** Dash (build-now wave 3): a burst at the top of the envelope for DASH_MS,
 *  then walk; the next dash waits DASH_CD_MS from the LAST dash's START; the
 *  first DASH_IFRAME_MS are a dodge — a scheduled blow that resolves inside
 *  them whiffs (the i-frame class of the traversal guide, §17.4). 11.5 m/s ×
 *  0.25 s ≈ 2.9 m, under the 5 m teleport threshold even at 4 steps per
 *  message, and inside the 12 m/s envelope (never 12 itself: validateMove's
 *  `>` cap would clip float error on the boundary). Sekiro/Bloodborne-class
 *  dodges are 0.17–0.53 s of invulnerability; 180 ms is the readable minimum. */
export const SPEED_DASH_MPS = 11.5;
export const DASH_MS = 250;
export const DASH_CD_MS = 900;
export const DASH_IFRAME_MS = 180;
/** Guard + parry (build-now wave 4): the right button HELD is the guard —
 *  a blow from inside the guard cone (±60°) lands for GUARD_DMG_MULT; the
 *  RAISE opens a parry window: a blow resolving inside it staggers the
 *  attacker and does nothing. Spam decays the window Sekiro-style (a raise
 *  within PARRY_DECAY_MS of the previous halves it, down to the floor);
 *  PARRY_DECAY_MS of not raising restores it. A parry is a read, not a
 *  rhythm. Judged on the scheduled snapshot with the row's own stamps. */
export const GUARD_CONE_COS = 0.5;
export const GUARD_DMG_MULT = 0.5;
export const PARRY_WINDOW_MS = 250;
export const PARRY_WINDOW_MIN_MS = 40;
export const PARRY_DECAY_MS = 500;
/** Hit-confirm (build-now wave 5): a swing that LANDED prices the next one
 *  cheaper — the mirror of the whiff's ×1.5 (Mordhau #22 both ways). */
export const HIT_CD_MULT = 0.7;
/** A bounty hunter's blow reaches this far (the client hunter AI's hit range). */
export const HUNTER_REACH_M = 2.3;
/** A hunter's reported position must lie this close to the player to be believed. */
export const HUNTER_PLAUSIBLE_M = 12;

// ── PvP + committed timing (ground PvP wave, 2026-09-18) ──────────────────
/** A weapon change takes this long to bring to bear: `equipReadyAt` on the
 *  actor row. Never shortens a paid swing recovery (`attackReadyAt`). */
export const EQUIP_MS = 350;
/** After going down, a player can neither harm nor be harmed by another
 *  player for this long — the arrival pad is not a hunting ground. */
export const RECOVERY_PROTECT_MS = 8000;
/** A hard stagger that just ended cannot be re-applied for this long
 *  (competitive profile): a heavy hit inside the window still hurts, it
 *  does not chain the stun. */
export const STAGGER_RESIST_MS = 1000;
/** A parry in a competitive context interrupts the attacker this long
 *  (a bounded initiative advantage, not the PvE 600 ms stun). */
export const PVP_PARRY_STAGGER_MS = 250;
/** Server-owned regeneration: quiet for REGEN_QUIET_MS, then REGEN_HP_PER_S
 *  (the old client-driven tick healed 4 hp per ~2 s — the same rate). */
export const REGEN_QUIET_MS = 10_000;
export const REGEN_HP_PER_S = 2;
/** Protected service areas: within this radius of a settlement's arrival pad
 *  no player may harm another (sanctuary outranks every context). */
export const SANCTUARY_R_M = 30;
/** Duels: the ring's radius, the countdown, the boundary grace, the limit,
 *  the invite's life and the disconnect grace. */
export const DUEL_RING_R_M = 14;
export const DUEL_RING_OFFSET_M = 60;
export const DUEL_INVITE_M = 120;
export const DUEL_COUNTDOWN_MS = 3000;
export const DUEL_BOUNDARY_MS = 3000;
export const DUEL_LIMIT_MS = 90_000;
export const DUEL_INVITE_TTL_MS = 30_000;
export const DUEL_OFFLINE_MS = 10_000;
export const DUEL_INVITE_GAP_MS = 10_000;
/** Relay Skirmish (arena): sizes 1..4 a side; rounds to 150 s; the relay
 *  wakes at 45 s (15 s after two drawn rounds); 20 s of uncontested hold
 *  captures it; 2 s vacant then 1 s/s decay; first to 3 rounds; 15 min cap;
 *  90 s first readiness, 45 s between rounds; 30 s reconnect grace. */
export const ARENA_ZONE_R_M = 32;
export const ARENA_ZONE_OFFSET_M = 110;
export const ARENA_STAGING_M = 24;
export const ARENA_STAGING_R_M = 8;
export const ARENA_RELAY_R_M = 6;
export const ARENA_ROUND_MS = 150_000;
export const ARENA_RELAY_AT_MS = 45_000;
export const ARENA_RELAY_AT_FAST_MS = 15_000;
export const ARENA_CAPTURE_S = 20;
export const ARENA_VACANT_MS = 2000;
export const ARENA_ROUNDS_TO_WIN = 3;
export const ARENA_MATCH_CAP_MS = 15 * 60_000;
export const ARENA_READY_FIRST_MS = 90_000;
export const ARENA_READY_BETWEEN_MS = 45_000;
export const ARENA_COUNTDOWN_MS = 5000;
export const ARENA_RECONNECT_MS = 30_000;
export const ARENA_QUEUE_M = 150;
/** Field (conflict sector) enrollment: opt-in countdown, combat lock, opt-out. */
export const FIELD_OPT_IN_MS = 5000;
export const FIELD_LOCK_MS = 15_000;
export const FIELD_LEAVE_MS = 10_000;
/** One objective receipt per sector per this window (the war adapter's clamp). */
export const FIELD_OBJECTIVE_WINDOW_MS = 10 * 60_000;
export const FIELD_CAPTURE_S = 20;

export type MoveMode = 0 | 1 | 2 | 3 | 4 | 5; // walk | sprint | slide | swim | crouch | dash
export const MODE_WALK: MoveMode = 0;
export const MODE_SPRINT: MoveMode = 1;
export const MODE_SLIDE: MoveMode = 2;
export const MODE_SWIM: MoveMode = 3;
export const MODE_CROUCH: MoveMode = 4;
export const MODE_DASH: MoveMode = 5;

/** Speed for a movement mode; a slide decays linearly from its peak to walk
 *  over SLIDE_MS and is over after that (the caller drops back to walk). */
export function moveSpeedFor(mode: MoveMode, slideElapsedMs: number, staggered = false): number {
  let v: number;
  if (mode === MODE_SPRINT) v = SPEED_SPRINT_MPS;
  else if (mode === MODE_SWIM) v = SPEED_SWIM_MPS;
  else if (mode === MODE_CROUCH) v = SPEED_CROUCH_MPS;
  else if (mode === MODE_DASH) v = SPEED_DASH_MPS;
  else if (mode === MODE_SLIDE) {
    const t = Math.min(1, Math.max(0, slideElapsedMs / SLIDE_MS));
    v = SLIDE_PEAK_MPS + (SPEED_WALK_MPS - SLIDE_PEAK_MPS) * t;
  } else v = SPEED_WALK_MPS;
  return staggered ? v * STAGGER_SPEED_MULT : v;
}

/** A slide is over once SLIDE_MS has elapsed. */
export function slideActive(slideElapsedMs: number): boolean {
  return slideElapsedMs >= 0 && slideElapsedMs < SLIDE_MS;
}

/** A dash is over once DASH_MS has elapsed. */
export function dashActive(dashElapsedMs: number): boolean {
  return dashElapsedMs >= 0 && dashElapsedMs < DASH_MS;
}

/** May a dash begin? Not staggered, not afloat, and DASH_CD_MS since the
 *  last dash STARTED (a never-dashed player passes any large elapsed). */
export function dashAllowed(msSinceLastDashStart: number, staggered: boolean, swimming: boolean): boolean {
  return !staggered && !swimming && msSinceLastDashStart >= DASH_CD_MS;
}

/** The dodge: a blow resolving inside the dash's first DASH_IFRAME_MS whiffs. */
export function dodgeActive(msSinceDashStart: number): boolean {
  return msSinceDashStart >= 0 && msSinceDashStart < DASH_IFRAME_MS;
}

/** Facing: the player's forward is (−sin yaw, −cos yaw) — the input module's
 *  convention. True when the target lies inside the cone. A target at the
 *  attacker's own position counts as faced (no direction to test). */
export function facingOk(yaw: number, ax: number, az: number, tx: number, tz: number, cosMin: number): boolean {
  const dx = tx - ax, dz = tz - az;
  const d = Math.hypot(dx, dz);
  if (d < 1e-3) return true;
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  return (fx * dx + fz * dz) / d >= cosMin;
}

/** Reach with the latency pad. */
export function inReach(ax: number, az: number, tx: number, tz: number, reachM: number): boolean {
  return Math.hypot(tx - ax, tz - az) <= reachM + REACH_PAD_M;
}

export function coneFor(kind: 'gun' | 'melee'): number {
  return kind === 'gun' ? GUN_CONE_COS : MELEE_CONE_COS;
}

/** The cooldown a swing must respect: ×WHIFF_CD_MULT when the LAST swing
 *  whiffed, ×HIT_CD_MULT when it LANDED (the hit-confirm branch). */
export function requiredCooldownMs(baseMs: number, lastWhiffed: boolean, lastLanded = false): number {
  if (lastWhiffed) return Math.round(baseMs * WHIFF_CD_MULT);
  if (lastLanded) return Math.round(baseMs * HIT_CD_MULT);
  return baseMs;
}

/** The parry window a raise opens, given the window the PREVIOUS raise had
 *  and how long ago it was: a raise inside PARRY_DECAY_MS halves the
 *  previous window (floored); one past it restores the full window. A first
 *  raise (no previous) gets the full window. */
export function parryWindowFor(prevWindowMs: number, msSincePrevRaise: number): number {
  if (!(msSincePrevRaise >= 0) || msSincePrevRaise >= PARRY_DECAY_MS || prevWindowMs <= 0) return PARRY_WINDOW_MS;
  return Math.max(PARRY_WINDOW_MIN_MS, Math.round(prevWindowMs / 2));
}

export type DefenseVerdict = 'dodged' | 'parried' | 'guarded' | 'hit';

export interface DefenseArgs {
  /** ms since the defender's dash began (< 0 = none). */
  msSinceDash: number;
  /** The guard is raised (guardRaisedAt > guardReleasedAt). */
  guarding: boolean;
  /** ms since the guard was raised (only read while guarding). */
  msSinceRaise: number;
  /** The parry window that raise opened. */
  parryWindowMs: number;
  /** The attacker lies inside the defender's guard cone. */
  facing: boolean;
}

/** The defender's side of the scheduled snapshot: dodge outranks the guard,
 *  a parry needs the raise to be fresh AND the attacker faced, a held guard
 *  facing the attacker halves the blow, anything else lands whole. */
export function judgeDefense(a: DefenseArgs): DefenseVerdict {
  if (dodgeActive(a.msSinceDash)) return 'dodged';
  if (!a.guarding || !a.facing) return 'hit';
  if (a.msSinceRaise >= 0 && a.msSinceRaise < a.parryWindowMs) return 'parried';
  return 'guarded';
}

/** A guarded blow's damage (never below 1 for a blow that was going to hurt). */
export function guardedDamage(dmg: number): number {
  return dmg > 0 ? Math.max(1, Math.round(dmg * GUARD_DMG_MULT)) : 0;
}

export type StrikeVerdict =
  | { kind: 'hit' }
  | { kind: 'whiff'; why: 'reach' | 'facing' | 'blocked' }
  | { kind: 'refused'; why: 'cooldown' | 'staggered' | 'slide' | 'dash' | 'equipping' | 'pending' };

export interface StrikeArgs {
  ax: number; az: number; yaw: number;
  tx: number; tz: number;
  reachM: number; weaponKind: 'gun' | 'melee';
  cooldownMs: number;
  /** ms since the last strike (any outcome) and whether that strike whiffed. */
  sinceLastStrikeMs: number; lastWhiffed: boolean;
  /** Whether the last strike LANDED (the hit-confirm: the next comes ×HIT_CD_MULT sooner). */
  lastLanded?: boolean;
  /** ms until the attacker's stagger ends (≤ 0 = not staggered). */
  staggerLeftMs: number;
  /** ms since a slide began (< 0 = not sliding). */
  slideElapsedMs: number;
  /** ms since a dash began (< 0 / absent = not dashing): no swing mid-dash —
   *  the dash owns the body for its whole DASH_MS (build-now wave 3). */
  dashElapsedMs?: number;
  /** Line of sight from attacker to target (ground PvP wave): false = a wall
   *  stands between them — the shot is spent on the wall (a 'blocked' whiff).
   *  Absent = clear (open country, or the caller has no grid). */
  losClear?: boolean;
  /** ms until the committed-recovery deadline passes (attackReadyAt − now;
   *  ≤ 0 / absent = ready). Judged BEFORE the legacy cadence: a paid swing
   *  keeps its price whatever weapon is in hand now. */
  attackReadyInMs?: number;
  /** ms until the weapon just equipped is brought to bear (equipReadyAt − now). */
  equipReadyInMs?: number;
  /** A committed attack (a startup phase) is still pending: no new swing. */
  pending?: boolean;
}

/** The one judgement every strike goes through, server and client alike.
 *  Refusals cost nothing; whiffs consume the swing and price the next one. */
export function judgeStrike(a: StrikeArgs): StrikeVerdict {
  if (a.staggerLeftMs > 0) return { kind: 'refused', why: 'staggered' };
  if (a.slideElapsedMs >= 0 && a.slideElapsedMs < SLIDE_LOCKOUT_MS) return { kind: 'refused', why: 'slide' };
  if (a.dashElapsedMs !== undefined && dashActive(a.dashElapsedMs)) return { kind: 'refused', why: 'dash' };
  if (a.pending) return { kind: 'refused', why: 'pending' };
  if (a.attackReadyInMs !== undefined && a.attackReadyInMs > 0) return { kind: 'refused', why: 'cooldown' };
  if (a.equipReadyInMs !== undefined && a.equipReadyInMs > 0) return { kind: 'refused', why: 'equipping' };
  if (a.sinceLastStrikeMs < requiredCooldownMs(a.cooldownMs, a.lastWhiffed, a.lastLanded === true)) return { kind: 'refused', why: 'cooldown' };
  if (!inReach(a.ax, a.az, a.tx, a.tz, a.reachM)) return { kind: 'whiff', why: 'reach' };
  if (!facingOk(a.yaw, a.ax, a.az, a.tx, a.tz, coneFor(a.weaponKind))) return { kind: 'whiff', why: 'facing' };
  if (a.losClear === false) return { kind: 'whiff', why: 'blocked' };
  return { kind: 'hit' };
}

/** The recovery a committed attack pays, as an ABSOLUTE interval from its
 *  start (ground PvP wave): a landed hit is ready at start + hitInterval; a
 *  miss (whiff / blocked / dodged / parried) at impact + the recovery portion
 *  × missMult — never earlier than the hit interval's own recovery floor. */
export function recoveryAfter(startupMs: number, hitIntervalMs: number, missMult: number, landed: boolean): number {
  const base = Math.max(0, hitIntervalMs - startupMs);
  return landed ? hitIntervalMs : startupMs + Math.round(base * missMult);
}

export function isHeavy(baseDmg: number): boolean {
  return baseDmg >= HEAVY_DMG_MIN;
}

/** NPC windup length for a blow: harder blows telegraph longer (up to 1.5 s). */
export function windupMsFor(dmg: number): number {
  const t = Math.min(1, Math.max(0, (dmg - 5) / 20)); // species dmg 5..22 → 0..1
  return Math.round(WINDUP_MIN_MS + (WINDUP_MAX_MS - WINDUP_MIN_MS) * t);
}

/** Can a windup still be staggered out of? Not once past the onset. */
export function windupStaggerable(windupElapsedMs: number): boolean {
  return windupElapsedMs < HYPER_ARMOUR_ONSET_MS;
}

/** Aggression tokens: may one more attacker wind up on this target? */
export function tokenFree(activeWindupsOnTarget: number): boolean {
  return activeWindupsOnTarget < AGGRO_TOKENS;
}

/** Client-side target pick among candidates: nearest INSIDE the cone and
 *  padded reach; the server judges the same way, so a pick here never whiffs
 *  on facing. Returns the index or -1. */
export function pickTarget(
  ax: number, az: number, yaw: number, reachM: number, kind: 'gun' | 'melee',
  cands: ReadonlyArray<{ x: number; z: number }>,
): number {
  let best = -1, bd = Infinity;
  const cosMin = coneFor(kind);
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    const d = Math.hypot(c.x - ax, c.z - az);
    if (d > reachM + REACH_PAD_M || d >= bd) continue;
    if (!facingOk(yaw, ax, az, c.x, c.z, cosMin)) continue;
    bd = d; best = i;
  }
  return best;
}

/** A STABLE pick (ground PvP wave): keep the current mark while it is still
 *  a legal candidate inside the envelope, unless another is MEANINGFULLY
 *  better — closer by more than SWITCH_GAIN of the current distance AND at
 *  least as well aligned. Candidates carry `legal` (policy + visibility —
 *  filtered BEFORE ranking) and an optional `key` for continuity. Returns
 *  the index or −1. */
export const SWITCH_GAIN = 0.25;
export function pickTargetStable(
  ax: number, az: number, yaw: number, reachM: number, kind: 'gun' | 'melee',
  cands: ReadonlyArray<{ x: number; z: number; legal?: boolean; key?: string }>, currentKey: string,
): number {
  const cosMin = coneFor(kind);
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  let best = -1, bd = Infinity, bAlign = -2, cur = -1, cd = Infinity, cAlign = -2;
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    if (c.legal === false) continue;
    const dx = c.x - ax, dz = c.z - az;
    const d = Math.hypot(dx, dz);
    if (d > reachM + REACH_PAD_M) continue;
    const align = d < 1e-3 ? 1 : (fx * dx + fz * dz) / d;
    if (align < cosMin) continue;
    if (c.key !== undefined && c.key === currentKey) { cur = i; cd = d; cAlign = align; }
    if (d < bd) { bd = d; best = i; bAlign = align; }
  }
  if (cur < 0 || best === cur) return best;
  return (bd < cd * (1 - SWITCH_GAIN) && bAlign >= cAlign - 1e-6) ? best : cur;
}

/** Server-owned regeneration math (ground PvP wave): hp after `nowMs` given
 *  the last evaluation, the last hurt, a fractional accumulator and whether
 *  healing is allowed at all (never inside a live contest). Returns the new
 *  hp, the new accumulator and the evaluation stamp to store. Reading twice
 *  heals once: the stamp advances to `nowMs` every call. */
export function regenStep(
  hp: number, maxHp: number, lastHurtMs: number, evaluatedMs: number, acc: number, nowMs: number, allowed: boolean,
): { hp: number; acc: number; evaluatedMs: number } {
  if (!allowed || hp >= maxHp) return { hp, acc: 0, evaluatedMs: nowMs };
  const from = Math.max(evaluatedMs, lastHurtMs + REGEN_QUIET_MS);
  const dt = Math.max(0, nowMs - from) / 1000;
  const gained = acc + dt * REGEN_HP_PER_S;
  const whole = Math.floor(gained);
  const next = Math.min(maxHp, hp + whole);
  return { hp: next, acc: next >= maxHp ? 0 : gained - whole, evaluatedMs: nowMs };
}

// ── Ammo (2026-09-05): magazines reload, capacitors recharge ───────────────
export type Feed =
  | { kind: 'magazine'; rounds: number; reloadMs: number }
  | { kind: 'energy'; chargeMax: number; shotCost: number; rechargePerS: number };

/** A capacitor's charge after `elapsedMs` of refilling (clamped to the max). */
export function chargeAfter(charge: number, feed: Extract<Feed, { kind: 'energy' }>, elapsedMs: number): number {
  return Math.min(feed.chargeMax, charge + feed.rechargePerS * Math.max(0, elapsedMs) / 1000);
}

/** ms until a capacitor can pay for one more shot (0 = now). */
export function msUntilCharged(charge: number, feed: Extract<Feed, { kind: 'energy' }>): number {
  return charge >= feed.shotCost ? 0 : Math.ceil((feed.shotCost - charge) / feed.rechargePerS * 1000);
}

export type FeedVerdict = { ok: true } | { ok: false; why: 'reloading' | 'recharging'; msLeft: number };

/** May the gun fire NOW? A magazine with rounds left fires; one mid-reload
 *  refuses with the time left; an empty one is refused for a full reload
 *  (the caller starts it). A capacitor fires when it can pay the shot. */
export function feedReady(feed: Feed, state: { rounds: number; charge: number; reloadLeftMs: number }): FeedVerdict {
  if (feed.kind === 'magazine') {
    if (state.reloadLeftMs > 0) return { ok: false, why: 'reloading', msLeft: state.reloadLeftMs };
    if (state.rounds <= 0) return { ok: false, why: 'reloading', msLeft: feed.reloadMs };
    return { ok: true };
  }
  const wait = msUntilCharged(state.charge, feed);
  return wait > 0 ? { ok: false, why: 'recharging', msLeft: wait } : { ok: true };
}

/** Shots a full capacitor holds (the HUD's pip count). */
export function shotsPerCharge(feed: Extract<Feed, { kind: 'energy' }>): number {
  return Math.floor(feed.chargeMax / feed.shotCost);
}
